import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation } from "react-router-dom";
import { useTran } from "@/hooks";
import { motion } from "framer-motion";
import { useAuthStore } from "../hooks";
import { PATHS } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { LanguageSwitcher } from "@/components/common";
import { LoginForm, RegisterForm } from "../components";

const AuthPage = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTran();
  const { toggleRegister, isRegister } = useAuthStore();

  // Sync with URL
  useEffect(() => {
    const path = location.pathname.toLowerCase();
    const isRegis = path === PATHS.REGISTER || path.endsWith("/register");
    toggleRegister(isRegis);
  }, [location.pathname, toggleRegister]);

  const goToLogin = () => navigate(PATHS.LOGIN);
  const goToRegister = () => navigate(PATHS.REGISTER);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      {/* Language Switcher */}
      <div className="absolute right-4 top-4 z-50">
        <LanguageSwitcher />
      </div>
      <div className="relative w-full max-w-3xl overflow-hidden rounded-[30px] bg-card shadow-2xl">
        <div className="relative flex min-h-[500px] flex-col md:flex-row">
          {/* Login Form - Slides from right to left */}
          <motion.div
            initial={false}
            animate={{
              x: isRegister ? "-100%" : "0%",
              opacity: isRegister ? 0 : 1,
            }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute right-0 top-0 z-20 flex h-full w-full items-center justify-center md:w-1/2"
            style={{ pointerEvents: isRegister ? "none" : "auto" }}
          >
            <LoginForm />
          </motion.div>

          {/* Register Form - Slides from left to right */}
          <motion.div
            initial={false}
            animate={{
              x: isRegister ? "0%" : "100%",
              opacity: isRegister ? 1 : 0,
            }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="absolute left-0 top-0 z-20 flex h-full w-full items-center justify-center md:w-1/2"
            style={{ pointerEvents: isRegister ? "auto" : "none" }}
          >
            <RegisterForm />
          </motion.div>

          {/* Gradient Overlay Panel */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div
              initial={false}
              animate={{ left: isRegister ? "50%" : "-250%" }}
              transition={{ duration: 1.0, ease: "easeInOut" }}
              className={`absolute -left-[250%] z-10 h-full w-[300%] rounded-radius bg-gradient-to-br from-primary to-secondary ${
                isRegister
                  ? "md:rounded-l-[170px] md:rounded-r-radius"
                  : "md:rounded-r-[170px] md:rounded-l-radius"
              }`}
            />

            {/* Login Welcome Text (Left side) */}
            <motion.div
              initial={false}
              animate={{
                y: isRegister ? "-30%" : "0%",
                opacity: isRegister ? 0 : 1,
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut",
                delay: isRegister ? 0 : 0.3,
              }}
              className="absolute left-0 top-0 z-30 flex h-full w-full flex-col items-center justify-center p-8 text-center md:w-1/2"
              style={{ pointerEvents: isRegister ? "none" : "auto" }}
            >
              <h1 className="mb-4 text-4xl font-bold text-white">
                {t("Hello, Welcome!")}
              </h1>
              <p className="max-w-md text-white">
                {t(
                  "Thank you for joining our journey! Together, we can create a better future. Explore, engage, and be a part of something meaningful.",
                )}
              </p>
            </motion.div>

            {/* Register Welcome Text (Right side) */}
            <motion.div
              initial={false}
              animate={{
                y: isRegister ? "0%" : "30%",
                opacity: isRegister ? 1 : 0,
              }}
              transition={{
                duration: 0.6,
                ease: "easeInOut",
                delay: isRegister ? 0.3 : 0,
              }}
              className="pointer-events-none absolute right-0 top-0 z-30 flex h-full w-full flex-col items-center justify-center p-8 text-center md:w-1/2"
            >
              <h1 className="mb-4 text-4xl font-bold text-white">
                {t("Welcome Back!")}
              </h1>
              <p className="mb-6 text-white">{t("Already have an account?")}</p>
              <Button
                onClick={goToLogin}
                variant="outline"
                className="pointer-events-auto rounded-full border-2 border-white bg-transparent text-white hover:bg-white/10"
              >
                {t("Login")}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default AuthPage;
